import {Box, HBox} from '@buncha/components/core/view';
import {CustomText, H5Text, H6Text} from '@buncha/components/core/text';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {B2BUserScreens} from '@buncha/B2B/navigation/types';
import {CustomImage} from '@buncha/components/core/Image';
import {DeliveryLocationStatus} from '@buncha/batchDetails/types';
import If from '@buncha/components/conditional/If';
import ImageModal from '../imageModal';
import {Modules} from '@buncha/utils/types';
import {OrderHistory} from '@buncha/B2B/hooks/type';
import StepHeader from '../stepHeader/StepHeader';
import {Touchable} from '@buncha/components/core/touchable';
import moment from 'moment';
import {styles} from './styles';
import {useGetLocale} from '@buncha/localization/hooks';

interface DeliveryStepProps {
  isActive: boolean;
  deliveryImages?: string[];
  orderHistory?: OrderHistory;
  failedOrderHistory?: OrderHistory;
  deliveryLocationStatus?: DeliveryLocationStatus;
  setScreenTitle?: React.Dispatch<React.SetStateAction<string>>;
}

const DeliveryStep: React.FC<DeliveryStepProps> = props => {
  const {
    isActive,
    deliveryImages,
    orderHistory,
    failedOrderHistory,
    deliveryLocationStatus,
    setScreenTitle,
  } = props;
  const muliFont = useFontTheme();
  const theme = useColorThemeType();
  const {title, body} =
    useGetLocale(Modules.B2BUserOrder, B2BUserScreens.OrderInfo) ?? {};
  const [imageModal, setImageModal] = useState(false);
  const [imageURL, setImageURL] = useState('');
  const imageUrls = deliveryImages || [];
  const isDelivered =
    deliveryLocationStatus === DeliveryLocationStatus.delivered;
  const isCancelled =
    deliveryLocationStatus === DeliveryLocationStatus.cancelled;
  const timeStamp = orderHistory?.createdAt.length
    ? moment(orderHistory?.createdAt).format('hh:mm a')
    : '';
  const failedTimeStamp = failedOrderHistory?.createdAt.length
    ? moment(orderHistory?.createdAt).format('hh:mm a')
    : '';
  const titleText = isDelivered ? body?.orderDelivered : body?.deliveryFailed;

  const toggleImageModal = useCallback(() => {
    setImageModal(!imageModal);
  }, [imageModal]);
  const onPress = useCallback(
    (url: string) => {
      const imageUrl = url?.replace('http:', 'https:') || '';
      setImageURL(imageUrl);
      toggleImageModal();
    },
    [toggleImageModal],
  );

  const getText = useMemo(() => {
    if (isCancelled) return title?.orderCancelledTitle;

    return isActive ? title?.deliveryArrived : title?.deliveryArrivedEnjoy;
  }, [
    isActive,
    isCancelled,
    title?.deliveryArrived,
    title?.deliveryArrivedEnjoy,
    title?.orderCancelledTitle,
  ]);

  useEffect(() => {
    if (isActive && setScreenTitle) setScreenTitle(titleText || '');
  }, [isActive, setScreenTitle, titleText]);

  return (
    <Box>
      <If condition={!isActive || isDelivered}>
        <StepHeader textTitle={getText} timeString={timeStamp} />
      </If>
      <If condition={isDelivered}>
        <If condition={isActive}>
          <Box>
            <CustomText style={styles.enjoyText}>{body?.enjoy}</CustomText>
            <HBox style={styles.imagesContainer}>
              {imageUrls?.map((url, index) => (
                <Touchable
                  onPress={() => onPress(url)}
                  key={index}
                  style={styles.imageTouchable}>
                  <CustomImage style={styles.imageStyle} source={url} />
                </Touchable>
              ))}
            </HBox>
          </Box>
        </If>
      </If>
      <If condition={!isDelivered && isActive}>
        <Box>
          <HBox>
            <H5Text
              style={{
                fontFamily: muliFont[600].normal,
                color: theme?.text.red[300],
              }}>
              {body?.deliveryFailed}
            </H5Text>
            <H6Text style={{fontFamily: muliFont[400].normal}}>
              {failedTimeStamp}
            </H6Text>
          </HBox>
          <HBox style={styles.container}>
            <CustomImage
              style={styles.imageVanStyle}
              source={require('@buncha/resource/images/NoVan.png')}
            />
            <Box style={styles.flexStyle}>
              <H6Text
                style={[
                  styles.alignText,
                  {
                    fontFamily: muliFont[600].normal,
                    color: theme?.text.blue[300],
                  },
                ]}>
                {body?.deliveryUnsuccessful}
              </H6Text>
              <CustomText
                style={[
                  styles.alignText,
                  {
                    fontFamily: muliFont[600].normal,
                    color: theme?.text.gray[100],
                  },
                ]}>
                {body?.supportAssistance}
              </CustomText>
            </Box>
          </HBox>
        </Box>
      </If>
      <If condition={imageModal}>
        <ImageModal
          open={imageModal}
          onClose={toggleImageModal}
          image={{uri: imageURL}}
        />
      </If>
    </Box>
  );
};

export default React.memo(DeliveryStep);
